import React, { useState } from "react";
import ResponsivePanel from "../Layout/ResponsivePanel";
// Import or define Button, Card, Badge, and icons as needed, or use simple HTML/JSX if not available
// Remove Next.js/TypeScript specifics

// Dummy UI components for now (replace with your own or install a UI kit)
const Button = ({ children, ...props }) => <button {...props}>{children}</button>;
const Card = ({ children, ...props }) => <div {...props}>{children}</div>;
const Badge = ({ children, ...props }) => <span {...props}>{children}</span>;
// Dummy icons (replace with your own icon library or SVGs)
const Icon = ({ name }) => <span style={{ fontWeight: 'bold' }}>{name}</span>;

// Node types (copied and adapted from zimzee-ad-platform, icons replaced with strings for now)
const nodeTypes = [
  // Input Modules
  { type: "audience", title: "Audience Selector", description: "Define your target audience", icon: "👥", color: "bg-blue-50 border-blue-200", inputs: [], outputs: ["audience"], category: "Input" },
  { type: "brand", title: "Brand Context", description: "Set brand guidelines and voice", icon: "🎯", color: "bg-purple-50 border-purple-200", inputs: [], outputs: ["brand"], category: "Input" },
  { type: "competitor", title: "Competitor Analysis", description: "Analyze competitor content", icon: "📈", color: "bg-red-50 border-red-200", inputs: [], outputs: ["competitor-data"], category: "Input" },
  { type: "keyword-research", title: "Keyword Research", description: "Research trending keywords", icon: "🔍", color: "bg-emerald-50 border-emerald-200", inputs: ["audience", "competitor-data"], outputs: ["keywords"], category: "Input" },
  { type: "data-source", title: "Data Source", description: "Import external data", icon: "🗄️", color: "bg-slate-50 border-slate-200", inputs: [], outputs: ["data"], category: "Input" },
  { type: "trend-analyzer", title: "Trend Analyzer", description: "Analyze market trends", icon: "📊", color: "bg-orange-50 border-orange-200", inputs: ["competitor-data"], outputs: ["trends"], category: "Input" },
  { type: "inspiration", title: "Inspiration Picker", description: "Select a Zimzee card for creative inspiration", icon: "✨", color: "bg-gradient-to-br from-orange-50 to-red-50 border-orange-200", inputs: [], outputs: ["inspiration"], category: "Input" },
  // Content Generation Modules
  { type: "ads", title: "Ads", description: "Generate social media ads", icon: "📢", color: "bg-orange-50 border-orange-200", inputs: ["audience", "brand"], outputs: ["ads"], category: "Generation" },
  { type: "emails", title: "Emails", description: "Create email campaigns", icon: "📧", color: "bg-green-50 border-green-200", inputs: ["audience", "brand"], outputs: ["emails"], category: "Generation" },
  { type: "social", title: "Social", description: "Generate social media posts", icon: "📱", color: "bg-cyan-50 border-cyan-200", inputs: ["audience", "brand"], outputs: ["social"], category: "Generation" },
  { type: "blog", title: "Blog", description: "Create blog content", icon: "📝", color: "bg-indigo-50 border-indigo-200", inputs: ["audience", "brand", "keywords"], outputs: ["blog-content"], category: "Generation" },
  { type: "video-script", title: "Video Script", description: "Generate video scripts", icon: "🎬", color: "bg-pink-50 border-pink-200", inputs: ["audience", "brand"], outputs: ["video-script"], category: "Generation" },
  { type: "landing-page", title: "Landing Page", description: "Create landing page copy", icon: "🌐", color: "bg-teal-50 border-teal-200", inputs: ["audience", "brand"], outputs: ["landing-page"], category: "Generation" },
  { type: "podcast-script", title: "Podcast Script", description: "Generate podcast episodes", icon: "🎤", color: "bg-amber-50 border-amber-200", inputs: ["audience", "brand"], outputs: ["podcast-script"], category: "Generation" },
  { type: "press-release", title: "Press Release", description: "Create PR content", icon: "📰", color: "bg-lime-50 border-lime-200", inputs: ["brand", "data"], outputs: ["press-release"], category: "Generation" },
  // Visual Content Modules
  { type: "image-generator", title: "Image Generator", description: "AI-generated images", icon: "🖼️", color: "bg-purple-50 border-purple-200", inputs: ["brand"], outputs: ["image"], category: "Visual" },
];

// Demo workflow (copied and adapted from zimzee-ad-platform)
const createDemoWorkflow = () => {
  const demoNodes = [
    { id: "audience-demo", type: "audience", title: "Audience Selector", description: "Define your target audience", icon: "👥", position: { x: 50, y: 100 }, inputs: [], outputs: ["audience"], status: "idle", config: { audienceType: "business", customAudience: "Marketing professionals aged 25-45" } },
    { id: "competitor-demo", type: "competitor", title: "Competitor Analysis", description: "Analyze competitor content", icon: "📈", position: { x: 50, y: 300 }, inputs: [], outputs: ["competitor-data"], status: "idle" },
    { id: "keyword-demo", type: "keyword-research", title: "Keyword Research", description: "Research trending keywords", icon: "🔍", position: { x: 350, y: 200 }, inputs: ["audience", "competitor-data"], outputs: ["keywords"], status: "idle" },
    { id: "brand-demo", type: "brand", title: "Brand Context", description: "Set brand guidelines and voice", icon: "🎯", position: { x: 50, y: 500 }, inputs: [], outputs: ["brand"], status: "idle", config: { brandName: "TechCorp", brandVoice: "professional", brandDescription: "B2B SaaS company" } },
    { id: "ads-demo", type: "ads", title: "Ads", description: "Generate social media ads", icon: "📢", position: { x: 650, y: 100 }, inputs: ["audience", "brand"], outputs: ["ads"], status: "idle", config: { adFormat: "square", adCount: "3", adObjective: "conversions" } },
    { id: "social-demo", type: "social", title: "Social", description: "Generate social media posts", icon: "📱", position: { x: 650, y: 300 }, inputs: ["audience", "brand"], outputs: ["social"], status: "idle" },
    { id: "blog-demo", type: "blog", title: "Blog", description: "Create blog content", icon: "📝", position: { x: 650, y: 500 }, inputs: ["audience", "brand", "keywords"], outputs: ["blog-content"], status: "idle" },
  ];
  const demoConnections = [
    { id: "conn-1", from: "audience-demo", to: "keyword-demo", fromOutput: "audience", toInput: "audience" },
    { id: "conn-2", from: "competitor-demo", to: "keyword-demo", fromOutput: "competitor-data", toInput: "competitor-data" },
    { id: "conn-3", from: "audience-demo", to: "ads-demo", fromOutput: "audience", toInput: "audience" },
    { id: "conn-4", from: "brand-demo", to: "ads-demo", fromOutput: "brand", toInput: "brand" },
    { id: "conn-5", from: "audience-demo", to: "social-demo", fromOutput: "audience", toInput: "audience" },
    { id: "conn-6", from: "brand-demo", to: "social-demo", fromOutput: "brand", toInput: "brand" },
    { id: "conn-7", from: "audience-demo", to: "blog-demo", fromOutput: "audience", toInput: "audience" },
    { id: "conn-8", from: "brand-demo", to: "blog-demo", fromOutput: "brand", toInput: "brand" },
    { id: "conn-9", from: "keyword-demo", to: "blog-demo", fromOutput: "keywords", toInput: "keywords" },
  ];
  return { nodes: demoNodes, connections: demoConnections };
};

const WorkflowBuilder = () => {
  // State
  const [nodes, setNodes] = useState([]);
  const [, setConnections] = useState([]);
  const [, setSelectedNode] = useState(null);
  useState(null);
  useState({ x: 0, y: 0 });
  const [draggedModuleType, setDraggedModuleType] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  // Tutorial steps
  const tutorialSteps = [
    { title: "Welcome to Workflow Builder", description: "Create custom content generation workflows by connecting modules together.", highlight: null },
    { title: "Add Modules", description: "Click on any module in the left panel to add it to your canvas, or drag it directly to where you want it.", highlight: "modules" },
    { title: "Connect Modules", description: "Click on an output circle and drag to any module to connect them. Release anywhere on the target module!", highlight: "connections" },
    { title: "Multiple Connections", description: "One output can connect to multiple inputs - perfect for creating content variations! For example, connect Audience and Competitor Analysis to Keyword Research.", highlight: "multiple" },
    { title: "Run Your Workflow", description: "Once connected, click 'Run Workflow' to execute your content generation pipeline.", highlight: "run" },
  ];

  // Add node
  const addNode = (type, position) => {
    const nodeType = nodeTypes.find((t) => t.type === type);
    if (!nodeType) return;
    const newNode = {
      id: `${type}-${Date.now()}`,
      type,
      title: nodeType.title,
      description: nodeType.description,
      icon: nodeType.icon,
      position,
      inputs: nodeType.inputs,
      outputs: nodeType.outputs,
      status: "idle",
      config: {},
    };
    setNodes((prev) => [...prev, newNode]);
  };

  // Drag and drop handlers
  const handleModuleDragStart = (e, moduleType) => {
    setDraggedModuleType(moduleType);
    e.dataTransfer.effectAllowed = "copy";
  };
  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const handleCanvasDrop = (e) => {
    e.preventDefault();
    if (!draggedModuleType) return;
    const canvasRect = e.currentTarget.getBoundingClientRect();
    const position = {
      x: e.clientX - canvasRect.left - 100,
      y: e.clientY - canvasRect.top - 50,
    };
    addNode(draggedModuleType, position);
    setDraggedModuleType(null);
  };

  // Top bar actions
  const handleLoadDemo = () => {
    const demo = createDemoWorkflow();
    setNodes(demo.nodes);
    setConnections(demo.connections);
  };
  const handleClearAll = () => {
    setNodes([]);
    setConnections([]);
    setSelectedNode(null);
  };

  // Render
  return (
    <div className="workflow-workbench flex h-screen bg-gray-50">
      {/* Top Header Bar */}
      <div className="workflow-header absolute top-0 left-0 right-0 z-40 bg-white border-b border-gray-200">
        <div className="workflow-header__inner flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <h1 className="workflow-header__title text-2xl font-bold text-gray-900">Workflow Builder</h1>
              <Badge className="bg-orange-50 text-orange-700 border-orange-200">{nodes.length} modules</Badge>
            </div>
          </div>
          <div className="workflow-header__actions flex items-center space-x-3">
            <Button className="workflow-mobile-library-toggle" onClick={() => setIsLibraryOpen(true)}>Modules</Button>
            <Button onClick={() => { setShowTutorial(true); setTutorialStep(0); }}>Help</Button>
            <Button onClick={handleLoadDemo}>Load Demo</Button>
            <Button onClick={handleClearAll} disabled={nodes.length === 0}>Clear All</Button>
            <Button disabled={nodes.length === 0}>Save Workflow</Button>
            <Button disabled={nodes.length === 0}>Run Workflow</Button>
          </div>
        </div>
      </div>
      {/* Left Panel - Module Palette */}
      <ResponsivePanel open={isLibraryOpen} onClose={() => setIsLibraryOpen(false)} side="left" label="Workflow module library" className="workflow-module-panel">
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col mt-20">
        <div className="p-4 border-b border-gray-200 bg-white">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Module Library</h2>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
            <h4 className="font-medium text-orange-900 text-sm mb-2">How to Use:</h4>
            <ol className="text-xs text-orange-800 space-y-1">
              <li>Drag modules to canvas or click to add</li>
              <li>Click on output circles to connect</li>
              <li>Release anywhere on target module!</li>
              <li>Click gear icon to configure modules</li>
              <li>Click eye icon to view results</li>
            </ol>
          </div>
          <div className="space-y-2">
            <Button onClick={handleClearAll} className="w-full" size="sm">Clear Canvas</Button>
            <Button onClick={() => { setShowTutorial(true); setTutorialStep(0); }} className="w-full" size="sm">Show Tutorial</Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {["Input", "Generation"].map((category) => (
              <div key={category}>
                <h3 className="font-semibold text-gray-900 mb-3">{category} Modules</h3>
                <div className="space-y-2">
                  {nodeTypes.filter((nodeType) => nodeType.category === category).map((nodeType) => (
                    <Card key={nodeType.type} className="p-3 cursor-pointer transition-all hover:shadow-md" draggable onDragStart={(e) => handleModuleDragStart(e, nodeType.type)} onClick={() => addNode(nodeType.type, { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 })}>
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                          <Icon name={nodeType.icon} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-sm text-gray-900">{nodeType.title}</h4>
                          <p className="text-xs text-gray-600">{nodeType.description}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </ResponsivePanel>
      {/* Canvas */}
      <div className="workflow-canvas flex-1 relative overflow-hidden mt-20" onDragOver={handleCanvasDragOver} onDrop={handleCanvasDrop}>
        {/* Canvas content here: render nodes, connections, etc. */}
        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Layers" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Building Your Workflow</h3>
              <p className="text-gray-600 mb-6">Drag modules from the left panel or click "Load Demo" to see a sample workflow in action.</p>
              <div className="space-y-2">
                <Button onClick={handleLoadDemo}>Load Demo Workflow</Button>
                <Button onClick={() => { setShowTutorial(true); setTutorialStep(0); }}>Show Tutorial</Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Tutorial Modal */}
      {showTutorial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="workflow-tutorial-dialog bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 relative">
            <button onClick={() => setShowTutorial(false)} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700">X</button>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{tutorialSteps[tutorialStep].title}</h2>
            <p className="text-gray-700 mb-6">{tutorialSteps[tutorialStep].description}</p>
            <div className="flex justify-between">
              <Button onClick={() => setTutorialStep((prev) => Math.max(0, prev - 1))} disabled={tutorialStep === 0}>Previous</Button>
              <Button onClick={() => { if (tutorialStep < tutorialSteps.length - 1) { setTutorialStep((prev) => prev + 1); } else { setShowTutorial(false); } }}>{tutorialStep === tutorialSteps.length - 1 ? "Close" : "Next"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowBuilder;
